// Stage 48 §18: typed API client for the operator-side /api/connections/*
// admin surface.

import { apiGet, apiJson } from './client';

export type Scope = 'image' | 'speech' | 'music' | 'video' | 'documents';

export const ALL_SCOPES: readonly Scope[] = [
  'image',
  'speech',
  'music',
  'video',
  'documents',
];

export interface ConnectionListRow {
  id: string;
  label: string;
  token_prefix: string;
  scopes: Scope[];
  allowed_origin: string;
  rate_limit_per_minute: number;
  expires_at: number | null;
  enabled: boolean;
  notes: string | null;
  created_at: number;
  updated_at: number;
  last_used_at: number | null;
  stats?: { total_ok: number; total_errors: number };
}

export interface AuditRow {
  id: string;
  ts: number;
  method: string;
  path: string;
  status_code: number;
  bytes_returned: number | null;
  origin: string | null;
  user_agent: string;
  request_id: string;
  error_kind: string | null;
}

export interface NewConnectionInput {
  label: string;
  scopes: Scope[];
  allowed_origin: string;
  rate_limit_per_minute?: number;
  expires_at?: number | null;
  notes?: string | null;
  enabled?: boolean;
}

export type EditConnectionInput = Partial<NewConnectionInput>;

export interface MintResponse {
  connection: ConnectionListRow;
  plaintext_token: string;
}

export const listConnections = () =>
  apiGet<{ connections: ConnectionListRow[] }>('/api/connections');

export const getConnection = (id: string) =>
  apiGet<{ connection: ConnectionListRow }>(
    `/api/connections/${encodeURIComponent(id)}`,
  );

export const createConnection = (body: NewConnectionInput) =>
  apiJson<MintResponse>('POST', '/api/connections', body);

export const updateConnection = (
  id: string,
  patch: EditConnectionInput,
) =>
  apiJson<{ connection: ConnectionListRow }>(
    'PATCH',
    `/api/connections/${encodeURIComponent(id)}`,
    patch,
  );

export const rotateConnection = (id: string) =>
  apiJson<MintResponse>(
    'POST',
    `/api/connections/${encodeURIComponent(id)}/rotate`,
    {},
  );

export const deleteConnection = (id: string) =>
  apiJson<{ deleted: string }>(
    'DELETE',
    `/api/connections/${encodeURIComponent(id)}`,
  );

export const listAudit = (id: string, before?: number) => {
  const qs = before ? `?before=${before}` : '';
  return apiGet<{ rows: AuditRow[]; nextCursor: string | null }>(
    `/api/connections/${encodeURIComponent(id)}/audit${qs}`,
  );
};
